/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import pearlApi from '../../../../../api/pearlApi';
import { RootState } from '../../../../../../../app/store';

type sectionDataType = { label: string; content: string };
type suggestionType = { name: string; _href: string };

type MedicationState = {
  index: number;
  medicationData: medicationDataType;
};
type medicationDataType = {
  result: {
    name: string;
    main_sections: sectionDataType[];
    extended_sections: sectionDataType[];
  };
  suggestions: suggestionType[];
  loadingMedicationData: boolean;
};

const initialState: MedicationState = {
  index: 0,
  medicationData: {
    result: {
      name: '',
      main_sections: [],
      extended_sections: [],
    },
    suggestions: [],
    loadingMedicationData: false,
  },
};

// Fetch Medication data
export const fetchMedicationData = createAsyncThunk<medicationDataType, { query: string }, { state: RootState }>(
  'fetchMedicationData',
  async ({ query }) => {
    try {
      const {
        data: { data },
      } = await pearlApi.get(`/search/meds?${query}`);
      return data;
    } catch (err) {
      console.log(`Failed to fetch medication data: ${(err as Error).message}`);
      return {
        result: {
          name: '',
          main_sections: [],
          extended_sections: [],
        },
        suggestions: [],
      };
    }
  }
);

export const medicationSlice = createSlice({
  name: 'medication',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setIndex: (state, action: PayloadAction<number>) => {
      state.index = action.payload;
    },
    clearMedicationState: (state) => {
      state.medicationData = {
        result: {
          name: '',
          main_sections: [],
          extended_sections: [],
        },
        suggestions: [],
        loadingMedicationData: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMedicationData.fulfilled, (state, data) => {
      state.medicationData = data.payload;
      state.medicationData.loadingMedicationData = false;
    });
    builder.addCase(fetchMedicationData.pending, (state) => {
      state.medicationData.loadingMedicationData = true;
    });
    builder.addCase(fetchMedicationData.rejected, (state) => {
      state.medicationData.loadingMedicationData = false;
    });
  },
});

export const { setIndex, clearMedicationState } = medicationSlice.actions;

export const selectMedicationState = (state: RootState): medicationDataType => state.medication.medicationData;

export default medicationSlice.reducer;
